import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import type { FunctionReference } from "convex/server";
import { api } from "@constructionautomation/backend";
import { Loader2, Plus, SearchCheck } from "lucide-react";

import { AppLink } from "@/components/app-link";
import { PageHeader } from "@/components/layout";
import { useProject } from "@/components/projects";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { formatRelativeTime } from "@/lib/utils";

export const Route = createFileRoute("/procurePilot/")({
  component: ProcurePilotPage,
});

type ProcurePilotJobSummary = {
  _id: string;
  name: string;
  status: string;
  itemCount?: number;
  createdAt: number;
};

type ProcurePilotJobsApi = {
  list: FunctionReference<"query", "public", { projectId?: string }, ProcurePilotJobSummary[]>;
};

const procurePilotApi = (api as unknown as { procurePilot: { jobs: ProcurePilotJobsApi } })
  .procurePilot.jobs;

function ProcureStatusBadge({ status }: { status: string }) {
  const className =
    status === "ready_for_review" || status === "completed"
      ? "border-green-500/40 bg-green-500/15 text-green-700 dark:text-green-300"
      : status === "enriching" || status === "schedule_processing"
        ? "border-blue-500/40 bg-blue-500/15 text-blue-700 dark:text-blue-300"
        : status === "failed"
          ? "border-red-500/40 bg-red-500/15 text-red-700 dark:text-red-300"
          : "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return (
    <Badge variant="outline" className={className}>
      {status.replaceAll("_", " ")}
    </Badge>
  );
}

export function ProcurePilotPage() {
  const navigate = useAppNavigate();
  const { projectId, project, isLoading: isProjectLoading } = useProject();
  const { data: jobs, isPending } = useQuery(
    convexQuery(procurePilotApi.list, projectId ? { projectId } : "skip")
  );
  const procureJobs = (jobs ?? []) as ProcurePilotJobSummary[];

  const handleNew = () => {
    navigate({ to: "/procurePilot/new", title: "New ProcurePilot" });
  };

  if (isProjectLoading || isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="ProcurePilot" description={project?.name ?? "Loading project..."} />
        <div className="flex items-center justify-center p-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="ProcurePilot"
        description={project?.name ?? "Select a project"}
        actions={
          <Button onClick={handleNew}>
            <Plus className="size-4" />
            New ProcurePilot Job
          </Button>
        }
      />

      {procureJobs.length === 0 ? (
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SearchCheck className="size-5 text-primary" />
              Start Procurement From a Schedule
            </CardTitle>
            <CardDescription>
              Select a schedule, choose the table rows to procure, then generate AI-assisted procurement items.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleNew}>
              <Plus className="size-4" />
              Create ProcurePilot Job
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border-2 border-border bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {procureJobs.map((job) => (
                <TableRow key={job._id}>
                  <TableCell>
                    <AppLink
                      to="/procurePilot/$jobId"
                      params={{ jobId: job._id }}
                      className="font-semibold text-primary hover:underline"
                    >
                      {job.name}
                    </AppLink>
                  </TableCell>
                  <TableCell><ProcureStatusBadge status={job.status} /></TableCell>
                  <TableCell className="text-right font-medium">{job.itemCount ?? 0}</TableCell>
                  <TableCell className="text-muted-foreground">{formatRelativeTime(job.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
